import React, { useState, useRef, memo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import useDropdownPosition from '../../../hooks/useDropdownPosition';

/**
 * ProgressCell - Progress dropdown cell with color coding
 * 
 * Props:
 * - value: Current progress value (0-100)
 * - rowIndex: Index of the row in the data array
 * - rowId: Unique ID of the row (for saving)
 * - columnId: ID of the column
 * - options: Array of selectable values (default: [0, 25, 50, 75, 100])
 * - hasEditPermission: Whether the user can edit this cell
 * - onUpdate: Callback to update local data (rowIndex, columnId, value)
 * - onSave: Callback to save to backend (rowId, columnId, oldValue, newValue) -> Promise
 * - isDragging: Whether drag-fill is currently active
 * - onDragFillStart: Legacy vertical-only drag-fill (e, rowIndex, columnId, value)
 * - onFillStart: Unified drag-fill — direction decided by drag motion (e, rowIndex, rowId, columnId, value)
 * - showDragHandle: Whether to show the drag-fill handle (default: true)
 * - isRetrying: Whether the save is being retried due to lock conflict
 */
const ProgressCell = memo(({ 
  value, 
  rowIndex, 
  rowId, 
  columnId, 
  options = [0, 25, 50, 75, 100],
  hasEditPermission = false,
  onUpdate,
  onSave,
  isDragging = false,
  onDragFillStart,
  onFillStart,
  showDragHandle = true,
  isRetrying = false,
  lockedByRoc = false,
  onLockedClick
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const triggerRef = useRef(null);
  const getPosition = useDropdownPosition({ menuHeight: 300, menuWidth: 80 });

  const getProgressStyle = useCallback((progress) => {
    if (progress >= 90) return { backgroundColor: '#d4edda', color: '#155724' };
    if (progress >= 50) return { backgroundColor: '#fff3cd', color: '#856404' };
    if (progress >= 25) return { backgroundColor: '#f8d7da', color: '#721c24' };
    return { backgroundColor: '#f8f9fa', color: '#6c757d' };
  }, []);

  const handleSelect = useCallback((newValue) => {
    setIsOpen(false);
    if (newValue !== value) {
      // Update local data immediately (using rowId for correct lookup in filtered/sorted data)
      if (onUpdate) {
        onUpdate(rowId, columnId, newValue);
      }
      
      // Save in background
      if (onSave) {
        setIsSaving(true);
        onSave(rowId, columnId, value, newValue)
          .then(() => setIsSaving(false))
          .catch(error => {
            setIsSaving(false);
            console.error('Background save failed:', error);
          });
      }
    }
  }, [value, rowId, columnId, onUpdate, onSave]);

  const handleClick = useCallback(() => {
    if (hasEditPermission) {
      setIsOpen(!isOpen);
    } else if (lockedByRoc && onLockedClick) {
      // Progress is locked because this discipline's Rules of Credit are not frozen yet.
      // Surface a clear message instead of silently doing nothing.
      onLockedClick();
    }
  }, [hasEditPermission, isOpen, lockedByRoc, onLockedClick]);

  // Single fill handle: prefer the unified handler (decides direction from drag motion);
  // fall back to the legacy vertical-only handler for cells that don't pass onFillStart.
  const handleDragStart = useCallback((e) => {
    e.stopPropagation(); // Prevent triggering cell click
    if (onFillStart) {
      onFillStart(e, rowIndex, rowId, columnId, value);
    } else if (onDragFillStart) {
      onDragFillStart(e, rowIndex, columnId, value);
    }
  }, [onFillStart, onDragFillStart, rowIndex, rowId, columnId, value]);

  return (
    <div 
      style={{ 
        position: 'relative', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        cursor: hasEditPermission && !isDragging ? 'pointer' : (lockedByRoc ? 'not-allowed' : 'default')
      }}
      onClick={handleClick}
    >
      <span
        ref={triggerRef}
        style={{
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: 'bold',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          cursor: hasEditPermission ? 'pointer' : (lockedByRoc ? 'not-allowed' : 'default'),
          userSelect: 'none',
          pointerEvents: isDragging ? 'none' : 'auto',
          opacity: lockedByRoc ? 0.6 : 1,
          ...getProgressStyle(value)
        }}
        title={hasEditPermission
          ? "Click to select value"
          : (lockedByRoc
              ? "Locked: Rules of Credit must be frozen by a PM/Technical Coordinator before progress can be entered"
              : "View only")}
      >
        {lockedByRoc && <span aria-hidden="true">🔒</span>}
        {value}%
        {(isSaving || isRetrying) && (
          <div 
            style={{
              width: '10px',
              height: '10px',
              border: `2px solid ${isRetrying ? '#ffe4c4' : '#fff'}`,
              borderTop: `2px solid ${isRetrying ? '#ff8c00' : '#007bff'}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}
            title={isRetrying ? 'Retrying save...' : 'Saving...'}
          ></div>
        )}
      </span>
      
      {isOpen && hasEditPermission && createPortal(
        <>
          <div
            onClick={() => setIsOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9998
            }}
          />
          {(() => {
            const pos = getPosition(triggerRef.current);
            return (
              <div
                style={{
                  position: 'fixed',
                  ...(pos.openUpward
                    ? { bottom: `${window.innerHeight - pos.top}px` }
                    : { top: `${pos.top}px` }),
                  ...(pos.right !== undefined
                    ? { right: `${pos.right}px` }
                    : { left: `${pos.left}px` }),
                  transform: pos.transform,
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                  zIndex: 9999,
                  minWidth: '80px',
                  maxHeight: `${Math.min(300, pos.maxHeight)}px`,
                  overflowY: 'auto'
                }}
              >
                {options.map(opt => (
                  <div
                    key={opt}
                    onClick={() => handleSelect(opt)}
                    style={{
                      padding: '6px 12px',
                      cursor: 'pointer',
                      backgroundColor: opt === value ? '#e7f3ff' : 'white',
                      fontSize: '11px',
                      textAlign: 'center',
                      borderBottom: '1px solid #f0f0f0',
                      color: '#333'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = opt === value ? '#e7f3ff' : 'white'}
                  >
                    {opt}%
                  </div>
                ))}
              </div>
            );
          })()}
        </>,
        document.body
      )}

      {/* Single fill handle (bottom-right): drag down to fill rows, sideways to fill progress columns */}
      {showDragHandle && hasEditPermission && (onFillStart || onDragFillStart) && (
        <div
          onMouseDown={handleDragStart}
          style={{
            position: 'absolute',
            bottom: '2px',
            right: '2px',
            width: '7px',
            height: '7px',
            backgroundColor: '#007bff',
            cursor: 'crosshair',
            borderRadius: '1px',
            zIndex: 10,
            boxShadow: '0 0 1px rgba(0,0,0,0.3)'
          }}
          title="Drag to fill — down for rows, sideways for progress columns"
        />
      )}
    </div>
  );
});

ProgressCell.displayName = 'ProgressCell';

export default ProgressCell;